import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import {
  Modal,
  Text,
  Button,
  Group,
  Stack,
  Paper,
  Title,
  Alert,
  ActionIcon,
  Switch,
  Badge,
  Tooltip,
  Loader,
} from "@mantine/core";
import {
  IconUser,
  IconMinus,
  IconPlus,
  IconUsersGroup,
} from "@tabler/icons-react";

function EditTeamRepsModal({ isOpen, onClose, team, onMembersUpdated }) {
  const { getVerifiedJWT } = useAuth();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [teamReps, setTeamReps] = useState([]);
  const [availableReps, setAvailableReps] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [isProcessingDownline, setIsProcessingDownline] = useState(false);

  const sortByName = users =>
    [...users].sort((a, b) => {
      const nameA = `${a.first_name || ""} ${a.last_name || ""}`
        .trim()
        .toLowerCase();
      const nameB = `${b.first_name || ""} ${b.last_name || ""}`
        .trim()
        .toLowerCase();
      const displayA = nameA || (a.name || a.email || "").toLowerCase();
      const displayB = nameB || (b.name || b.email || "").toLowerCase();
      return displayA.localeCompare(displayB);
    });

  const fetchAvailableReps = async () => {
    if (!team?.selling_year_id) {
      setAvailableReps([]);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const jwt = await getVerifiedJWT();
      const response = await fetch(
        getApiEndpoint(
          `/v1/selling-year/${team.selling_year_id}/available-user-seasons`
        ),
        {
          headers: { Authorization: `Bearer ${jwt}` },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to load available reps");
      }

      const result = await response.json();
      const data = result.data ?? result;
      setAvailableReps(sortByName(Array.isArray(data) ? data : []));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setHasChanges(false);
      setTeamReps(sortByName(team?.reps || []));
      fetchAvailableReps();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, team?.id, team?.selling_year_id]);

  const handleAddRep = async availableRep => {
    if (!team?.id || !availableRep?.user_season_id) return;

    setError(null);
    setTeamReps(prev => sortByName([...prev, availableRep]));
    setAvailableReps(prev =>
      sortByName(
        prev.filter(u => u.user_season_id !== availableRep.user_season_id)
      )
    );
    setHasChanges(true);

    try {
      const jwt = await getVerifiedJWT();
      const response = await fetch(getApiEndpoint("/v1/teams/user"), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          team_id: team.id,
          user_season_id: availableRep.user_season_id,
          include: true,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to add rep to team");
      }
    } catch (err) {
      setError(err.message);
      setTeamReps(prev =>
        prev.filter(u => u.user_season_id !== availableRep.user_season_id)
      );
      setAvailableReps(prev => sortByName([...prev, availableRep]));
    }
  };

  const handleRemoveRep = async rep => {
    if (!team?.id) return;

    setError(null);
    setTeamReps(prev =>
      prev.filter(u =>
        rep.user_season_id
          ? u.user_season_id !== rep.user_season_id
          : u.user_id !== rep.user_id
      )
    );
    if (rep.user_season_id) {
      setAvailableReps(prev => sortByName([...prev, rep]));
    }
    setHasChanges(true);

    try {
      const jwt = await getVerifiedJWT();
      const response = await fetch(getApiEndpoint("/v1/teams/user"), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          team_id: team.id,
          user_season_id: rep.user_season_id,
          include: false,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to remove rep from team");
      }
    } catch (err) {
      setError(err.message);
      setTeamReps(prev => sortByName([...prev, rep]));
      if (rep.user_season_id) {
        setAvailableReps(prev =>
          prev.filter(u => u.user_season_id !== rep.user_season_id)
        );
      }
    }
  };

  const handleToggleIncludeDownline = async user => {
    if (!user?.user_id || !team?.id) {
      setError("Unable to toggle: required data not available.");
      return;
    }

    const newValue = !user.include_downline;

    setTeamReps(prev =>
      prev.map(u =>
        u.user_id === user.user_id ? { ...u, include_downline: newValue } : u
      )
    );
    setHasChanges(true);
    setError(null);

    if (newValue) {
      setIsProcessingDownline(true);
    }

    try {
      const jwt = await getVerifiedJWT();
      const response = await fetch(getApiEndpoint("/v1/teams/user"), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          team_id: team.id,
          user_season_id: user.user_season_id,
          include: true,
          include_downline: newValue,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to update include downline setting");
      }
    } catch (err) {
      setTeamReps(prev =>
        prev.map(u =>
          u.user_id === user.user_id ? { ...u, include_downline: !newValue } : u
        )
      );
      setError(err.message);
    } finally {
      setIsProcessingDownline(false);
    }
  };

  const handleClose = () => {
    setError(null);
    if (hasChanges && onMembersUpdated) {
      onMembersUpdated();
    }
    onClose();
  };

  const getDisplayName = user => {
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    return user.name || user.email || "Unknown User";
  };

  return (
    <Modal
      opened={isOpen}
      onClose={handleClose}
      title={`Edit Reps - ${team?.name || "Team"}`}
      size="lg"
      centered
    >
      <Stack gap="lg">
        {error && (
          <Alert variant="light" color="red" title="Error">
            {error}
          </Alert>
        )}

        {loading ? (
          <Group justify="center" py="xl">
            <Loader size="md" />
            <Text>Loading reps...</Text>
          </Group>
        ) : (
          <>
            <Paper p="md" withBorder>
              <Group justify="space-between" align="center" mb="md">
                <Group gap="xs">
                  <Title order={4}>Current Reps ({teamReps.length})</Title>
                  {isProcessingDownline && <Loader size="xs" color="grape" />}
                </Group>
                <Text size="xs" c="dimmed" fw={500}>
                  Include Downline
                </Text>
              </Group>
              {teamReps.length > 0 ? (
                <Stack gap="sm">
                  {teamReps.map((user, index) => (
                    <Group
                      key={user.user_id || index}
                      gap="xs"
                      p="xs"
                      style={{
                        borderRadius: "6px",
                        backgroundColor: "var(--mantine-color-gray-0)",
                      }}
                    >
                      <IconUser size={16} color="var(--mantine-color-blue-6)" />
                      <Stack gap={1} style={{ flex: 1 }}>
                        <Group gap="xs">
                          <Text fw={500} size="sm">
                            {getDisplayName(user)}
                          </Text>
                          {user.include_downline && (
                            <Badge
                              size="xs"
                              color="grape"
                              variant="light"
                              leftSection={<IconUsersGroup size={10} />}
                            >
                              Includes Downline
                            </Badge>
                          )}
                        </Group>
                        {user.email && (
                          <Text size="xs" c="dimmed">
                            {user.email}
                          </Text>
                        )}
                      </Stack>
                      <Tooltip
                        label={
                          user.include_downline
                            ? "Disable auto-add downline"
                            : "Enable auto-add downline"
                        }
                        position="left"
                      >
                        <Switch
                          size="sm"
                          checked={user.include_downline || false}
                          onChange={() => handleToggleIncludeDownline(user)}
                          color="grape"
                        />
                      </Tooltip>
                      <ActionIcon
                        size="sm"
                        variant="light"
                        color="red"
                        onClick={() => handleRemoveRep(user)}
                      >
                        <IconMinus size={14} />
                      </ActionIcon>
                    </Group>
                  ))}
                </Stack>
              ) : (
                <Text c="dimmed" style={{ fontStyle: "italic" }}>
                  No reps on this team
                </Text>
              )}
            </Paper>

            <Paper p="md" withBorder>
              <Title order={4} mb="md">
                Available Reps ({availableReps.length})
              </Title>
              {availableReps.length > 0 ? (
                <Stack gap="sm">
                  {availableReps.map((user, index) => (
                    <Group
                      key={user.user_season_id || user.user_id || index}
                      gap="xs"
                      p="xs"
                      style={{
                        borderRadius: "6px",
                        backgroundColor: "var(--mantine-color-gray-0)",
                      }}
                    >
                      <IconUser size={16} color="var(--mantine-color-gray-6)" />
                      <Stack gap={1} style={{ flex: 1 }}>
                        <Group gap={4}>
                          <Text fw={500} size="sm">
                            {getDisplayName(user)}
                          </Text>
                          {user.season_name && (
                            <Text size="sm" c="dimmed" fs="italic">
                              ({user.season_name})
                            </Text>
                          )}
                        </Group>
                        {user.email && (
                          <Text size="xs" c="dimmed">
                            {user.email}
                          </Text>
                        )}
                      </Stack>
                      <ActionIcon
                        size="sm"
                        variant="light"
                        color="green"
                        onClick={() => handleAddRep(user)}
                      >
                        <IconPlus size={14} />
                      </ActionIcon>
                    </Group>
                  ))}
                </Stack>
              ) : (
                <Text c="dimmed" style={{ fontStyle: "italic" }}>
                  No additional reps available
                </Text>
              )}
            </Paper>
          </>
        )}

        <Group justify="flex-end" gap="sm">
          <Button variant="light" onClick={handleClose}>
            Close
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export default EditTeamRepsModal;
